import { useState, useEffect } from 'react';
import { useURLSync } from './hooks/useURLSync';
import { useNavigation } from './context/NavigationContext';
import { useUI } from './context/UIContext';
import { useAuth } from './context/AuthContext.tsx';
import { Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import HomeIntelligence from './components/home/HomeIntelligence';
import ProductTour from './components/layout/ProductTour';
import WelcomeModal from './components/layout/WelcomeModal';
import { CreateWorkspaceModal } from './components/CreateWorkspaceModal';
import { CommandPalette } from './components/common/CommandPalette';
import GlobalVoiceActivator from './components/GlobalVoiceActivator';
import { PlatformRouter } from './app/PlatformRouter';

// Initialize theme
if (typeof window !== 'undefined') {
  const isDarkStored = localStorage.getItem('theme') === 'dark';
  if (isDarkStored) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

export default function App() {
  const { user, dbUser, loading, activeWorkspace, isSwitchingWorkspace, isCreateWorkspaceModalOpen, setIsCreateWorkspaceModalOpen } = useAuth();
  const { currentView, setCurrentView } = useUI();
  const { globalFilters, setGlobalFilters } = useNavigation();
  
  useURLSync(currentView, setCurrentView, globalFilters, setGlobalFilters);

  // UI State
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [restartTour, setRestartTour] = useState(0);
  const [showWelcome, setShowWelcome] = useState(false);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeys = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key?.toLowerCase() === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleGlobalKeys);
    return () => window.removeEventListener('keydown', handleGlobalKeys);
  }, []);

  // Show welcome modal after onboarding
  useEffect(() => {
    const isOnboardingFinished = activeWorkspace?.settings?.onboardingCompleted === true;
    const justFinishedOnboarding = sessionStorage.getItem('just_finished_onboarding') === 'true';

    if (user && !loading && isOnboardingFinished && !justFinishedOnboarding && !sessionStorage.getItem('welcome_modal_shown')) {
      const timer = setTimeout(() => {
        setShowWelcome(true);
        sessionStorage.setItem('welcome_modal_shown', 'true');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [user, loading, activeWorkspace]);

  // Global event listeners
  useEffect(() => {
    const handleRestartTour = () => setRestartTour(prev => prev + 1);
    const handleToggleChat = () => setIsChatOpen(prev => !prev);
    const handleOpenChat = () => setIsChatOpen(true);
    const handleOpenCommandPalette = () => setIsCommandPaletteOpen(true);

    window.addEventListener('restart-tour', handleRestartTour);
    window.addEventListener('toggle-cyzor-chat', handleToggleChat);
    window.addEventListener('open-cyzor-chat', handleOpenChat);
    window.addEventListener('open-command-palette', handleOpenCommandPalette);

    return () => {
      window.removeEventListener('restart-tour', handleRestartTour);
      window.removeEventListener('toggle-cyzor-chat', handleToggleChat);
      window.removeEventListener('open-cyzor-chat', handleOpenChat);
      window.removeEventListener('open-command-palette', handleOpenCommandPalette);
    };
  }, []);

  // Chat component
  const ChatComponent = (
    <HomeIntelligence onClose={() => setIsChatOpen(false)} />
  );

  // Main platform router
  return (
    <>
      <PlatformRouter
        isSidebarCollapsed={isSidebarCollapsed}
        toggleSidebar={() => setIsSidebarCollapsed(prev => !prev)}
        isCommandPaletteOpen={isCommandPaletteOpen}
        setIsCommandPaletteOpen={setIsCommandPaletteOpen}
        isChatOpen={isChatOpen}
        setIsChatOpen={setIsChatOpen}
        ChatComponent={ChatComponent}
      />

      {/* Global overlays */}
      {user && <GlobalVoiceActivator />}

      {/* Chat FAB */}
      <div className="fixed bottom-24 lg:bottom-8 right-6 lg:right-8 z-50 flex flex-col items-end gap-4 pointer-events-none">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsChatOpen(!isChatOpen)}
          className="w-14 h-14 bg-black hover:bg-blue-600 text-white rounded-full flex items-center justify-center shadow-2xl border border-white/10 relative group pointer-events-auto"
          aria-label="Cyzor Intelligence Chat"
        >
          <span className="absolute inset-0 rounded-full bg-black/10 animate-ping group-hover:bg-blue-600/10 pointer-events-none" />
          {isChatOpen ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <Sparkles size={22} className="animate-pulse" />
          )}
        </motion.button>
      </div>

      {/* Product tour */}
      {user && !dbUser?.isPlatformAdmin && currentView === 'dashboard' && (
        <ProductTour key={restartTour} forceStart={restartTour > 0} />
      )}

      {/* Welcome modal */}
      <WelcomeModal 
        isOpen={showWelcome} 
        onClose={() => setShowWelcome(false)} 
        onUpgrade={() => {
          setShowWelcome(false);
          setCurrentView('configuracoes');
        }} 
      />

      {/* Create workspace modal */}
      <CreateWorkspaceModal 
        isOpen={isCreateWorkspaceModalOpen} 
        onClose={() => setIsCreateWorkspaceModalOpen(false)} 
      />

      {/* Command palette */}
      <CommandPalette 
        isOpen={isCommandPaletteOpen} 
        onClose={() => setIsCommandPaletteOpen(false)} 
        onNavigate={(v) => setCurrentView(v)} 
      />

      {/* Workspace switch overlay */}
      {isSwitchingWorkspace && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-[#111111]/20 border-t-[#111111] rounded-full animate-spin" />
            <p className="text-sm font-bold text-[#111111]">Carregando workspace...</p>
          </div>
        </div>
      )}
    </>
  );
}
